import { useQuery } from "@tanstack/react-query";
import {
  DailyStampCount,
  InactiveMerchant,
  MerchantWithStats,
  OverviewStats,
  RecentReward,
  RecentStamp,
  StampsByStore,
  UserGrowthPoint,
  statsService,
} from "src/services/statsService";
import { keys, staleTimes } from "./keys";

/**
 * Platform-wide admin analytics.
 *
 * The date-ranged hooks below are the ones the old in-memory Map cache in
 * statsService could not cache at all: its keys were flat strings with no way to
 * express parameters, so flipping a filter and flipping back always refetched.
 * Putting the range in the query key fixes that — returning to a range you have
 * already looked at is now free.
 */

export const useOverviewStats = () =>
  useQuery<OverviewStats>({
    queryKey: keys.stats.overview(),
    staleTime: staleTimes.stats,
    queryFn: () => statsService.getOverviewStats(),
  });

export const useUserGrowth = () =>
  useQuery<UserGrowthPoint[]>({
    queryKey: keys.stats.userGrowth(),
    staleTime: staleTimes.stats,
    queryFn: () => statsService.getUserGrowth(),
  });

export const useInactiveMerchants = () =>
  useQuery<InactiveMerchant[]>({
    queryKey: keys.stats.inactiveMerchants(),
    staleTime: staleTimes.stats,
    queryFn: () => statsService.getInactiveMerchants(),
  });

export const useMerchantsWithStats = () =>
  useQuery<MerchantWithStats[]>({
    queryKey: keys.stats.merchants(),
    staleTime: staleTimes.stats,
    queryFn: () => statsService.getMerchantsWithStats(),
  });

export const useMerchantsForMap = (
  period: "daily" | "weekly" | "monthly" | "yearly"
) =>
  useQuery<MerchantWithStats[]>({
    queryKey: keys.stats.merchantsForMap(period),
    staleTime: staleTimes.stats,
    queryFn: () => statsService.getMerchantsForMap(period),
  });

export const useDailyStamps = (startDate?: string, endDate?: string) =>
  useQuery<DailyStampCount[]>({
    queryKey: keys.stats.dailyStamps(startDate, endDate),
    staleTime: staleTimes.stats,
    queryFn: () => statsService.getDailyStampPurchases(startDate, endDate),
  });

export const useStampsByStore = (startDate?: string, endDate?: string) =>
  useQuery<StampsByStore[]>({
    queryKey: keys.stats.stampsByStore(startDate, endDate),
    staleTime: staleTimes.stats,
    queryFn: () => statsService.getStampsByStore(startDate, endDate),
  });

export const useRecentStamps = (limit = 50, date?: string) =>
  useQuery<RecentStamp[]>({
    queryKey: keys.stats.recentStamps(limit, date),
    staleTime: staleTimes.stats,
    queryFn: () => statsService.getRecentStamps(limit, date),
  });

export const useRecentRewards = (limit = 50) =>
  useQuery<RecentReward[]>({
    queryKey: keys.stats.recentRewards(limit),
    staleTime: staleTimes.stats,
    queryFn: () => statsService.getRecentRewards(limit),
  });
